import { Avatar, Box, Button, Card, Group, Skeleton, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { getInitialsFromName, reportAvatarLoadError, resolveAvatarSrc } from '@/utils/avatar';
import { WORKLOAD_LIST_SIZE } from '../constants';
import { BAR_FILL_COLOR, BAR_HEIGHT, BAR_RADIUS, BAR_TRACK_COLOR } from './BreakdownBars';

/**
 * WorkloadTable — who is carrying how much.
 *
 * Used twice on the Overview: leader caseload and facilitator load. Both answer the same
 * question, so they share one component; the caller supplies the column labels and the row
 * route.
 *
 * The load bar reuses `BreakdownBars`' geometry and colours so the two sections read as one system:
 * one hue, solid fill, length measured against the busiest person. No opacity ramp — length already
 * carries the magnitude, and fading it made the lightest-loaded rows unreadable on the dark theme.
 */
export interface WorkloadRow {
  userId: string;
  fullName?: string | null;
  avatar?: string | null;
  /** Primary load measure — drives the bar and the sort. */
  primary: number;
  /** Second column, e.g. in-progress cases or lead assignments. */
  secondary?: number;
  /** Third column, e.g. stale cases or courses. */
  tertiary?: number;
}

export interface WorkloadTableProps {
  title: string;
  subtitle?: string;
  rows: WorkloadRow[];
  loading?: boolean;
  primaryLabel: string;
  secondaryLabel?: string;
  tertiaryLabel?: string;
  /** Prefix the row's `userId` is appended to, e.g. `/Information/facilitators/`. */
  rowRoutePrefix: string;
  viewAllTo?: string;
  emptyMessage?: string;
}

/** Matches `BreakdownBars` — a zero-load row must still read as an (empty) bar. */
const MIN_BAR_PERCENT = 4;

export default function WorkloadTable({
  title,
  subtitle,
  rows,
  loading = false,
  primaryLabel,
  secondaryLabel,
  tertiaryLabel,
  rowRoutePrefix,
  viewAllTo,
  emptyMessage = 'No one is assigned yet.',
}: WorkloadTableProps) {
  const navigate = useNavigate();

  const sorted = [...rows].sort((a, b) => b.primary - a.primary);
  const shown = sorted.slice(0, WORKLOAD_LIST_SIZE);
  const max = Math.max(1, ...shown.map((r) => r.primary));

  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <div>
          <Text fw={600}>{title}</Text>
          {subtitle && <Text size="sm" c="dimmed">{subtitle}</Text>}
        </div>
        {viewAllTo && (
          <Button
            variant="subtle"
            size="compact-xs"
            rightSection={<IconArrowRight size={13} />}
            onClick={() => navigate(viewAllTo)}
          >
            View all
          </Button>
        )}
      </Group>

      {loading && rows.length === 0 ? (
        <Stack gap="xs">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={34} radius="sm" />
          ))}
        </Stack>
      ) : shown.length === 0 ? (
        <Text size="sm" c="dimmed">{emptyMessage}</Text>
      ) : (
        <Box style={{ overflowX: 'auto' }}>
          <Table highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th style={{ width: '32%' }}>{primaryLabel}</Table.Th>
                {secondaryLabel && <Table.Th style={{ textAlign: 'right' }}>{secondaryLabel}</Table.Th>}
                {tertiaryLabel && <Table.Th style={{ textAlign: 'right' }}>{tertiaryLabel}</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {shown.map((row) => {
                const avatarSrc = resolveAvatarSrc(row.avatar);
                const name = row.fullName ?? '—';
                const width = Math.max(MIN_BAR_PERCENT, (row.primary / max) * 100);

                return (
                  <Table.Tr
                    key={row.userId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`${rowRoutePrefix}${row.userId}`)}
                  >
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <Avatar
                          size={28}
                          radius="xl"
                          src={avatarSrc}
                          alt={name}
                          imageProps={{ onError: () => reportAvatarLoadError(avatarSrc) }}
                        >
                          {getInitialsFromName(row.fullName)}
                        </Avatar>
                        <Text size="sm" style={{ whiteSpace: 'nowrap' }}>{name}</Text>
                      </Group>
                    </Table.Td>

                    <Table.Td>
                      <Tooltip label={`${row.primary} ${primaryLabel.toLowerCase()}`} withArrow>
                        <Group gap="xs" wrap="nowrap">
                          <Box
                            style={{
                              flex: 1,
                              height: BAR_HEIGHT,
                              borderRadius: BAR_RADIUS,
                              backgroundColor: BAR_TRACK_COLOR,
                              overflow: 'hidden',
                              minWidth: 60,
                            }}
                          >
                            <Box
                              style={{
                                height: '100%',
                                width: `${width}%`,
                                borderRadius: BAR_RADIUS,
                                backgroundColor: BAR_FILL_COLOR,
                              }}
                            />
                          </Box>
                          <Text size="sm" fw={700} style={{ minWidth: 20, textAlign: 'right' }}>
                            {row.primary}
                          </Text>
                        </Group>
                      </Tooltip>
                    </Table.Td>

                    {secondaryLabel && (
                      <Table.Td style={{ textAlign: 'right' }}>{row.secondary ?? 0}</Table.Td>
                    )}
                    {tertiaryLabel && (
                      <Table.Td style={{ textAlign: 'right' }}>{row.tertiary ?? 0}</Table.Td>
                    )}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Card>
  );
}
